// ABOUTME: Claims grid on the relation shipments page
// ABOUTME: Rows open the claim sidepanel where the shipper's released replies are read

import { Box, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';

import { tokens } from "../../../../theme";
import ShDataGrid2 from "../../../../components/theme/datagrid2/ShDataGrid2";
import ShLabel from "../../../../components/theme/label/ShLabel";
import ShTextSkeleton from "../../../../components/theme/skeleton/ShTextSkeleton";
import ShLabelSkeleton from "../../../../components/theme/skeleton/ShLabelSkeleton";
import Text from "../../../../components/theme/text/Text";
import SidepanelLoader from "../../../../components/theme/sidepanel2/SidepanelLoader";
import { getClaimStatusLabeldata } from "../../../../utils/labelColorText";
import { useGetClaimsQuery } from "../../../../newapi/claim/claimSlice";
import { RELATION_CLAIM_FILTER_KEYS } from "../../../../components/global/DataGrid/Filter";
import SHQuickAction from "../../../../components/theme/Modal/SHQuickAction";
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const SpClaimDetails = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpClaimDetails/SpClaimDetails"));
const SpShipmentDetails = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpShipmentDetails/SpShipmentDetails"));
const SpOrderDetails = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpOrderDetails/SpOrderDetails"));

const gridOptions = {
    export: true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    filter: [RELATION_CLAIM_FILTER_KEYS.CLAIM_STATUS],
};

const Claims = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // The grid's config is its request body, never the tab config: it is serialised into
    // the query, and the tab config carries React elements.
    const [config, setConfig] = useState();
    const [isExtended, setIsExtended] = useState(false);
    const [spLoading, setSpLoading] = useState();

    const [claimId, setClaimId] = useState();
    const claimIdRef = useRef(claimId);

    useEffect(() => {
        props.replaceView?.({
            ...props.config,
            gridOptions: gridOptions,
        });
    }, [props.config]);

    const [shipmentId, setShipmentId] = useState();
    const shipmentIdRef = useRef(shipmentId);
    const [shipmentIsExtended, setShipmentIsExtended] = useState(false);
    const [shipmentSpLoading, setShipmentSpLoading] = useState();

    const getShipmentDetails = (id) => {
        if (!id) return;

        const newShipmentId = parseInt(id);
        if (shipmentIdRef.current !== newShipmentId) {
            if (!shipmentSpLoading) {
                setShipmentSpLoading(true);
            }
            setShipmentId(newShipmentId);
            shipmentIdRef.current = newShipmentId;
        }
        setShipmentIsExtended(true);
    };

    const [orderId, setOrderId] = useState();
    const orderIdRef = useRef(orderId);
    const [orderIsExtended, setOrderIsExtended] = useState(false);
    const [orderSpLoading, setOrderSpLoading] = useState();

    const getOrderDetails = (id) => {
        if (!id) return;

        const newOrderId = parseInt(id);
        if (orderIdRef.current !== newOrderId) {
            if (!orderSpLoading) {
                setOrderSpLoading(true);
            }
            setOrderId(newOrderId);
            orderIdRef.current = newOrderId;
        }
        setOrderIsExtended(true);
    };

    const getClaimDetails = (params) => {
        if (!params?.row?.id || params.row.skeleton) return;

        const newClaimId = parseInt(params.row.id);
        if (claimIdRef.current !== newClaimId) {
            if (!spLoading) {
                setSpLoading(true);
            }
            setClaimId(newClaimId);
            claimIdRef.current = newClaimId;
        }
        setIsExtended(true);
    };

    const columns = [
        {
            field: "trackingNumber",
            headerName: t("tracking_number"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                if (!params.row.shipment?.trackingNumber) return <Text>-</Text>;

                return (
                    <Box
                        sx={{ height: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}
                        // The row itself opens the claim, so the link has to keep the click.
                        onClick={(event) => { event.stopPropagation(); getShipmentDetails(params.row.shipment.id); }}
                    >
                        <Text bold clickable={true} variant={theme.palette.mode === "dark" ? "blue" : "primary"}>
                            {params.row.shipment.trackingNumber}
                        </Text>
                    </Box>
                );
            }
        },
        {
            field: "order",
            headerName: t("order"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                if (!params.row.order?.barcode) return <Text>-</Text>;

                return (
                    <Box
                        sx={{ height: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}
                        onClick={(event) => { event.stopPropagation(); getOrderDetails(params.row.order.id); }}
                    >
                        <Text bold clickable={true} variant={theme.palette.mode === "dark" ? "blue" : "primary"}>
                            {params.row.order.barcode}
                        </Text>
                    </Box>
                );
            }
        },
        {
            field: "shipper",
            headerName: t("shipper"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                return <Text>{params.row.shipperName ?? "-"}</Text>;
            }
        },
        {
            field: "reason",
            headerName: t("reason"),
            flex: 2,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                return <Text>{params.row.reason ?? "-"}</Text>;
            }
        },
        {
            field: "lastReply",
            headerName: t("last_reply"),
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;

                const lastReply = params.row.responses?.[0];
                if (!lastReply) return <Text light>-</Text>;

                const body = lastReply.modifiedBody || lastReply.body || "";
                const received = lastReply.receivedAt ? new Date(lastReply.receivedAt) : null;

                return (
                    <SHQuickAction
                        maxWidth={700}
                        component={
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, p: 2 }}>
                                <Text bold>{received ? received.toLocaleString(i18n.language) : "-"}</Text>
                                <Text sx={{ whiteSpace: "pre-wrap" }}>{body}</Text>
                            </Box>
                        }
                    >
                        {({ anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (
                            <Box
                                aria-owns={anchorEl ? "simple-popover" : undefined}
                                aria-haspopup="true"
                                onMouseEnter={onMouseEnter}
                                onMouseLeave={onMouseLeave}
                                onTouchStart={onTouchStart}
                                sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
                            >
                                <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
                                <Text>{received ? received.toLocaleDateString(i18n.language) : "-"}</Text>
                            </Box>
                        )}
                    </SHQuickAction>
                );
            }
        },
        {
            field: "claimStatus",
            headerName: t("status"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShLabelSkeleton />;

                const { labelcolor, labeltext } = getClaimStatusLabeldata(params.row.claimStatus, theme);
                return (
                    <ShLabel size={32} palette="normal" variant={labelcolor}>
                        {t(labeltext)}
                    </ShLabel>
                );
            }
        },
        {
            field: "createdAt",
            headerName: t("created_at"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                const date = params.row.createdAt ? new Date(params.row.createdAt) : null;
                return <Text>{date ? date.toLocaleDateString(i18n.language) : "-"}</Text>;
            }
        },
        {
            field: "phoneView",
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: () => {}
        },
    ];

    return (
        <Box height={1} display={"flex"} flexDirection={"column"}>
            <ShDataGrid2 key={theme.palette.mode}
                content={useGetClaimsQuery}
                config={config}
                title={t("claims")}
                gridOptions={gridOptions}
                sortModel={{ field: "createdAt", type: "DESC" }}
                columns={columns}
                onRowClick={getClaimDetails}>
            </ShDataGrid2>

            {spLoading &&
                <SpClaimDetails
                    claimId={claimId}
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                />
            }

            {shipmentSpLoading &&
                <SpShipmentDetails
                    shipmentId={shipmentId}
                    isExtended={shipmentIsExtended}
                    setIsExtended={setShipmentIsExtended}
                />
            }

            {orderSpLoading &&
                <SpOrderDetails
                    orderId={orderId}
                    isExtended={orderIsExtended}
                    setIsExtended={setOrderIsExtended}
                />
            }
        </Box>
    );
};

export default Claims;
